import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiActivity, FiUsers, FiDollarSign,
  FiTrendingUp, FiClock, FiCheckCircle,
  FiAlertCircle, FiRefreshCw, FiAward,
  FiTarget, FiBarChart2, FiShield, FiPieChart, FiBriefcase
} from 'react-icons/fi';
import { BsCurrencyRupee, BsGraphUp } from 'react-icons/bs';
import { Icon } from "@iconify/react/dist/iconify.js";
import dashboardServices from "../services/getdasboradServices";
import { getAdminWithdrawals } from "../services/adminWithdrawalService";
import { getPayments } from "../services/paymentService";
import "./DashBoardLayerOne.css";

/* ── Sparkline SVG ── */
const Sparkline = ({ color = '#fff', variant = 'line' }) => {
  if (variant === 'bar') {
    const bars = [40, 65, 45, 80, 55, 90, 70, 85, 60, 95, 75, 100];
    return (
      <svg viewBox="0 0 120 40" className="db-sparkline" preserveAspectRatio="none">
        {bars.map((h, i) => (
          <rect key={i} x={i * 11 + 2} y={40 - h * 0.38} width={8} height={h * 0.38}
            fill={color} opacity={0.45 + (i / bars.length) * 0.55} rx={2} />
        ))}
      </svg>
    );
  }
  const pts = [18, 32, 22, 42, 28, 50, 38, 54, 33, 58, 44, 56];
  const d = pts.map((y, i) => `${i === 0 ? 'M' : 'L'} ${i * 10} ${60 - y}`).join(' ');
  const area = `${d} L ${(pts.length - 1) * 10} 60 L 0 60 Z`;
  return (
    <svg viewBox="0 0 110 60" className="db-sparkline" preserveAspectRatio="none">
      <defs>
        <linearGradient id={`g${color.replace(/[#(),. ]/g, '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.45" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#g${color.replace(/[#(),. ]/g, '')})`} />
      <path d={d} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

/* ── Hero stat card ── */
const HeroCard = ({ title, value, sub, icon: IconComp, gradient, sparkVariant = 'line', onClick }) => (
  <div className="db-hero-card" style={{ background: gradient }} onClick={onClick}
    role={onClick ? 'button' : undefined}>
    <div className="db-hero-top">
      <div className="db-hero-icon"><IconComp size={18} /></div>
      <div className="db-hero-badge">{title}</div>
    </div>
    <div className="db-hero-value">{value ?? 0}</div>
    {sub && <div className="db-hero-sub">{sub}</div>}
    <div className="db-hero-sparkline">
      <Sparkline color="rgba(255,255,255,0.9)" variant={sparkVariant} />
    </div>
  </div>
);

/* ── Metric card ── */
const MetricCard = ({ title, value, sub, icon: IconComp, color }) => (
  <div className="db-metric-card">
    <div className="db-metric-icon" style={{ background: `${color}14`, color }}>
      <IconComp size={16} />
    </div>
    <div className="db-metric-value" style={{ color }}>{value ?? 0}</div>
    <div className="db-metric-label">{title}</div>
    {sub && <div className="db-metric-sub">{sub}</div>}
    <div className="db-metric-accent" style={{ background: color }} />
  </div>
);

/* ── Section block ── */
const Section = ({ title, badge, icon: IconComp, color, children, reportPath }) => {
  const navigate = useNavigate();
  return (
    <div className="db-section">
      <div className="db-section-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div className="db-section-icon" style={{ background: `${color}14`, color }}>
            <IconComp size={15} />
          </div>
          <div>
            <div className="db-section-title">{title}</div>
            {badge && (
              <span className="db-section-badge" style={{ background: `${color}14`, color }}>
                {badge}
              </span>
            )}
          </div>
        </div>
        {reportPath && (
          <button className="db-view-link" style={{ color }} onClick={() => navigate(reportPath)}>
            View Reports →
          </button>
        )}
      </div>
      <div className="db-metric-grid">{children}</div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════ */

const DashBoardLayerOne = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [matchStats, setMatchStats] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [questionBets, setQuestionBets] = useState(null);
  const [betsDashboard, setBetsDashboard] = useState(null);
  const [cSelection, setCSelection] = useState(null);
  const [pointsReport, setPointsReport] = useState(null);

  const [pendingDeposits, setPendingDeposits] = useState(0);
  const [pendingWithdrawals, setPendingWithdrawals] = useState(0);
  const prevPendingRef = useRef(null);

  const playNotificationSound = useCallback(() => {
    try {
      // eslint-disable-next-line no-undef
      const AudioCtx = window.AudioContext || window["webkitAudioContext"];
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.12);
      gain.gain.setValueAtTime(0.28, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.45);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.45);
    } catch (_) {}
  }, []);

  const fetchPendingCounts = useCallback(async () => {
    try {
      const [wRes, dRes] = await Promise.all([
        getAdminWithdrawals("pending"),
        getPayments(),
      ]);
      const wCount = wRes?.status ? (wRes.data?.length || 0) : 0;
      const dCount = dRes?.success
        ? (dRes.data || []).filter((r) => r.status === "requested").length
        : 0;

      setPendingWithdrawals(wCount);
      setPendingDeposits(dCount);

      const total = wCount + dCount;
      const prev = prevPendingRef.current;
      // Play on first load if pending > 0, or when count increases
      if (total > 0 && (prev === null || total > prev)) {
        playNotificationSound();
      }
      prevPendingRef.current = total;
    } catch (_) {}
  }, [playNotificationSound]);

  useEffect(() => {
    fetchPendingCounts();
    const id = setInterval(fetchPendingCounts, 10000);
    return () => clearInterval(id);
  }, [fetchPendingCounts]);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [matches, users, qBets, bDash, cSelect, pReport] = await Promise.all([
        dashboardServices.getMatchStats(),
        dashboardServices.getUserStats(),
        dashboardServices.getRevenueStats(),
        dashboardServices.getBetsDashboard(),
        dashboardServices.getCSelectionDashboard(),
        dashboardServices.getPointsGameReport()
      ]);
      setMatchStats(matches.dashboard);
      setUserStats(users);
      setQuestionBets(qBets.stats);
      setBetsDashboard(bDash.stats);
      setCSelection(cSelect.stats);
      setPointsReport(pReport.report);
    } catch (err) {
      setError(err.message || 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDashboardData(); }, []);

  const fmt = (n) => {
    const num = Number(n);
    return isNaN(num) ? "0" : num.toLocaleString('en-IN');
  };

  if (loading) {
    return (
      <div className="db-loading">
        <div className="db-spinner" />
        <span>Loading analytics…</span>
      </div>
    );
  }

  const today = new Date().toLocaleDateString('en-IN', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });

  const quickActions = [
    { label: 'Add User',    icon: 'solar:user-plus-bold',            color: '#4B5EAA', to: '/add-user' },
    { label: 'New Deposit', icon: 'solar:wallet-money-bold',         color: '#1da144', to: '/payment-list',     badge: pendingDeposits },
    { label: 'Withdrawals', icon: 'solar:transfer-horizontal-bold',  color: '#F0A500', to: '/withdrawals-list', badge: pendingWithdrawals },
    { label: 'Add Match',   icon: 'mingcute:soccer-bold',            color: '#3B9FE8', to: '/add-match' },
    { label: 'Reports',     icon: 'mingcute:report-bold',            color: '#7B6CF6', to: '/all-reports' },
    { label: 'UPI Manage',  icon: 'solar:card-bold',                 color: '#e05252', to: '/upi-manage' },
  ];

  return (
    <div className="db-root">

      {/* ── Top bar ── */}
      <div className="db-topbar">
        <div className="db-topbar-left">
          <div className="db-page-title">Dashboard</div>
          <div className="db-page-date">{today}</div>
        </div>
        <div className="db-topbar-right">
          <div className="db-live-pill">
            <span className="db-live-dot" />
            Live
          </div>
          <button className="db-refresh-btn" onClick={fetchDashboardData}>
            <FiRefreshCw size={13} />
            Refresh
          </button>
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="db-error">
          <FiAlertCircle size={15} />
          <span style={{ flex: 1 }}>{error}</span>
          <button className="db-retry-btn" onClick={fetchDashboardData}>
            <FiRefreshCw size={12} /> Retry
          </button>
        </div>
      )}

      <div className="db-stack">

        {/* ── Hero row ── */}
        {matchStats && (
          <div className="db-hero-grid">
            <HeroCard title="Total Matches" value={fmt(matchStats.total)}
              icon={FiBarChart2}
              gradient="linear-gradient(135deg,#4B5EAA,#6B7FCC)"
              onClick={() => navigate('/match-list')} />
            <HeroCard title="Live Now" value={fmt(matchStats.live)}
              icon={FiActivity}
              gradient="linear-gradient(135deg,#1a7a38,#26c45e)"
              sparkVariant="bar"
              onClick={() => navigate('/match-list')} />
            <HeroCard title="Upcoming" value={fmt(matchStats.upcoming)}
              icon={FiClock}
              gradient="linear-gradient(135deg,#d4890a,#f7c036)"
              sparkVariant="bar" />
            <HeroCard title="Total Users" value={fmt(userStats?.total_users)}
              icon={FiUsers}
              gradient="linear-gradient(135deg,#c0392b,#e8635a)"
              onClick={() => navigate('/users-list')} />
          </div>
        )}

        {/* ── Quick Actions ── */}
        <div className="db-qa-wrap">
          <div className="db-qa-label">Quick Actions</div>
          <div className="db-qa-row">
            {quickActions.map((a) => (
              <button key={a.label} className="db-qa-btn" onClick={() => navigate(a.to)}
                style={{ '--c': a.color, position: 'relative', overflow: 'visible' }}>
                <span className="db-qa-icon" style={{ background: `${a.color}15`, color: a.color }}>
                  <Icon icon={a.icon} style={{ fontSize: 18 }} />
                </span>
                <span className="db-qa-text">{a.label}</span>
                {!!a.badge && a.badge > 0 && (
                  <span style={{
                    position: 'absolute', top: -8, right: -8,
                    background: '#e05252', color: '#fff',
                    borderRadius: '50%', minWidth: 20, height: 20,
                    fontSize: '0.62rem', fontWeight: 800,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '0 5px', lineHeight: 1,
                    boxShadow: '0 2px 6px rgba(224,82,82,0.55)',
                    zIndex: 10, pointerEvents: 'none',
                  }}>
                    {a.badge > 99 ? '99+' : a.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ── Section A — Bets ── */}
        {betsDashboard && (
          <Section title="Section A — Bets Overview" badge="Live" icon={FiBriefcase}
            color="#7B6CF6" reportPath="/section-a-reports">
            <MetricCard title="Total Bets"  value={fmt(betsDashboard.total_bets)}          icon={FiTarget}       color="#7B6CF6" />
            <MetricCard title="Bet Amount"  value={`₹${fmt(betsDashboard.total_bet_amount)}`} icon={BsCurrencyRupee} color="#3B9FE8" />
            <MetricCard title="Won Amount"  value={`₹${fmt(betsDashboard.total_won_amount)}`}
              sub={`${betsDashboard.total_won_bets} Bets Won`}       icon={FiAward}        color="#1da144" />
            <MetricCard title="Net P&L"     value={`₹${fmt(betsDashboard.net_profit_loss)}`}
              icon={FiTrendingUp}
              color={Number(betsDashboard.net_profit_loss) >= 0 ? "#1da144" : "#e05252"} />
            <MetricCard title="Pending"     value={fmt(betsDashboard.total_pending_bets)}  icon={FiClock}        color="#F0A500" />
          </Section>
        )}

        {/* ── Section A — Question Bets ── */}
        {questionBets && (
          <Section title="Section A — Question Bets" icon={BsGraphUp} color="#1da144">
            <MetricCard title="Total Bets"    value={fmt(questionBets.total_bets)}          icon={FiTarget}       color="#1da144" />
            <MetricCard title="Total Amount"  value={`₹${fmt(questionBets.total_amount)}`}  icon={BsCurrencyRupee} color="#3B9FE8" />
            <MetricCard title="Team A Bets"   value={fmt(questionBets.total_a_bets)}
              sub={`₹${fmt(questionBets.total_a_amount)}`}           icon={FiShield}       color="#4B5EAA" />
            <MetricCard title="Team B Bets"   value={fmt(questionBets.total_b_bets)}
              sub={`₹${fmt(questionBets.total_b_amount)}`}           icon={FiShield}       color="#F0A500" />
            <MetricCard title="Pending"       value={fmt(questionBets.total_pending)}       icon={FiClock}        color="#94a3b8" />
          </Section>
        )}

        {/* ── Section B — Points Game ── */}
        {pointsReport && (
          <Section title="Section B — Points Game" icon={FiPieChart} color="#e05252"
            reportPath="/section-b-reports">
            <MetricCard title="Total Games"   value={fmt(pointsReport.total_games)}         icon={FiActivity}     color="#e05252" />
            <MetricCard title="Collection"    value={`₹${fmt(pointsReport.total_collection)}`} icon={BsCurrencyRupee} color="#3B9FE8" />
            <MetricCard title="Admin Profit"  value={`₹${fmt(pointsReport.admin_profit)}`}  icon={FiTrendingUp}   color="#1da144" />
            <MetricCard title="Payouts"       value={`₹${fmt(pointsReport.total_payout)}`}  icon={FiDollarSign}   color="#e05252" />
            <MetricCard title="Entries"       value={fmt(pointsReport.total_entries)}
              sub={`Won ${fmt(pointsReport.total_won)} · Lost ${fmt(pointsReport.total_lost)}`}
              icon={FiUsers} color="#4B5EAA" />
          </Section>
        )}

        {/* ── Section C — Selection ── */}
        {cSelection && (
          <Section title="Section C — Selection Settlement" icon={FiCheckCircle} color="#3B9FE8"
            reportPath="/section-c-reports">
            <MetricCard title="Total Games"       value={fmt(cSelection.total_games)}              icon={FiActivity}     color="#3B9FE8" />
            <MetricCard title="Collection"        value={`₹${fmt(cSelection.total_collection)}`}   icon={BsCurrencyRupee} color="#4B5EAA" />
            <MetricCard title="Distributable"     value={`₹${fmt(cSelection.total_distributable)}`} icon={FiTrendingUp}  color="#1da144" />
            <MetricCard title="Admin Commission"  value={`₹${fmt(cSelection.total_admin_commission)}`} icon={FiDollarSign} color="#F0A500" />
            <MetricCard title="Payout Given"      value={`₹${fmt(cSelection.total_payout_given)}`}
              sub={`${fmt(cSelection.total_winners)} Winners`}        icon={FiAward}        color="#1da144" />
          </Section>
        )}

      </div>
    </div>
  );
};

export default DashBoardLayerOne;
